import posthog from "posthog-js";

// Re-request consent because v2 adds email to identified person profiles.
export const CONSENT_KEY = "digitomy:analytics-consent:v2";
const ROUTES = new Set(["/", "/auth", "/pending", "/dashboard", "/annotate", "/captures", "/contact"]);
const EVENTS = new Set([
  "$pageview", "login_succeeded", "login_failed", "registration_succeeded",
  "registration_failed", "auth_sync_failed", "logout_succeeded",
  "tour_started", "tour_completed", "tour_skipped", "tour_dismissed",
  "annotation_session_started", "annotation_session_exhausted", "annotation_session_ended",
  "annotation_image_displayed", "annotation_submitted", "annotation_failed", "annotation_skipped",
  "queue_request_completed", "capture_page_loaded", "capture_page_failed",
  "capture_pagination_used", "image_viewer_opened", "image_load_completed", "image_load_failed",
]);
const PROPERTY_KEYS = new Set([
  "route", "organization_id", "role", "environment", "is_test", "tour_id", "tour_version",
  "trigger", "duration_ms", "status", "error_code", "page", "session_count",
  "source", "annotation_type", "consensus_reached", "image_other_locked", "reason",
]);

export const safeRoute = (path) => ROUTES.has(path) ? path : "/other";
export const errorCode = (error) => {
  const code = error?.code;
  if (typeof code === "string" && /^(auth\/[a-z-]+|ERR_[A-Z_]+)$/.test(code)) return code;
  const status = error?.response?.status;
  return Number.isInteger(status) ? `http_${status}` : "unexpected_error";
};

const profileEmail = (email) => {
  if (typeof email !== "string") return null;
  const trimmed = email.trim();
  return trimmed.length <= 320 && /^[^\s@]+@[^\s@?#/]+\.[^\s@?#/]+$/.test(trimmed) ? trimmed : null;
};

const safeProperties = (properties = {}) => Object.fromEntries(
  Object.entries(properties).filter(([key, value]) =>
    PROPERTY_KEYS.has(key) &&
    ((typeof value === "string" && value.length <= 100 && !/[?@]|:\/\//.test(value)) ||
      (typeof value === "number" && Number.isFinite(value)) || typeof value === "boolean")
  ),
);

const scrubMetadata = (value) => {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(scrubMetadata);
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !/url|referr|pathname|host|search|hash|title|email|password|token|explanation/i.test(key))
    .map(([key, item]) => [key, scrubMetadata(item)]));
};

// A small adapter keeps PostHog failures and unapproved properties out of app workflows.
export const createAnalytics = ({ client, token, host, enabled, environment, storage, location }) => {
  let initialized = false;
  let consent = null;
  let profile = null;
  let identifiedId = null;
  const listeners = new Set();
  try {
    const stored = storage?.getItem(CONSENT_KEY);
    consent = stored === "granted" || stored === "denied" ? stored : null;
  } catch { /* Consent still works in memory when storage is unavailable. */ }

  const context = () => ({
    environment,
    is_test: environment !== "production",
    ...(profile ? { organization_id: profile.organizationId, role: profile.role } : {}),
  });
  const personProperties = () => ({
    ...safeProperties(context()),
    ...(profile?.email ? { email: profile.email } : {}),
  });
  const identify = () => {
    if (!initialized || consent !== "granted" || !profile || identifiedId === profile.id) return;
    client.identify(profile.id, personProperties());
    identifiedId = profile.id;
  };
  const initialize = () => {
    if (!enabled || !token || !host || initialized || consent !== "granted") return;
    try {
      client.init(token, {
        api_host: host,
        defaults: "2026-05-30",
        person_profiles: "identified_only",
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        capture_dead_clicks: false,
        capture_performance: false,
        capture_exceptions: false,
        disable_session_recording: true,
        disable_surveys: true,
        disable_product_tours: true,
        disable_conversations: true,
        advanced_disable_flags: true,
        capture_webmcp: false,
        logs: { captureConsoleLogs: false },
        opt_out_capturing_by_default: true,
        enable_recording_console_log: false,
        save_campaign_params: false,
        save_referrer: false,
        respect_dnt: true,
        before_send: (event) => {
          if (consent !== "granted" ||
              (!EVENTS.has(event.event) && !["$identify", "$set"].includes(event.event))) return null;
          const properties = scrubMetadata(event.properties || {});
          // Email is allowed only as an explicit, verified person property.
          // Continue removing it from event metadata and arbitrary nested objects.
          if (["$identify", "$set"].includes(event.event) && profile?.email &&
              event.properties?.distinct_id === profile.id &&
              event.properties?.$set?.email === profile.email) {
            properties.$set = { ...properties.$set, email: profile.email };
          }
          // The public ingestion token is required by the PostHog protocol.
          properties.token = token;
          const route = safeRoute(location.pathname);
          properties.$current_url = `${location.origin}${route}`;
          properties.$pathname = route;
          return { ...event, properties };
        },
      });
      initialized = true;
      // Stored analytics identity does not prove the current session is authenticated.
      client.reset();
      client.opt_in_capturing();
      identify();
    } catch { /* Analytics must never interrupt the application. */ }
  };
  const capture = (event, properties) => {
    if (!enabled || consent !== "granted" || !EVENTS.has(event)) return;
    try {
      initialize();
      if (initialized) client.capture(event, safeProperties({ ...properties, ...context() }));
    } catch { /* Analytics must never interrupt the application. */ }
  };

  return {
    enabled: Boolean(enabled && token && host),
    client,
    initialize,
    capture,
    getConsent: () => consent,
    subscribe: (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
    setConsent: (value) => {
      if (!["granted", "denied"].includes(value)) return;
      consent = value;
      try { storage?.setItem(CONSENT_KEY, value); } catch { /* In-memory choice still applies. */ }
      try {
        if (value === "denied" && initialized) {
          client.reset();
          client.opt_out_capturing();
          identifiedId = null;
        } else if (value === "granted") {
          if (initialized) client.opt_in_capturing();
          initialize();
          identify();
        }
      } catch { /* Preference changes must not break the UI. */ }
      listeners.forEach((listener) => listener());
    },
    // Call only with a verified /auth/sync or /auth/me profile, never cached data.
    setAuthenticatedProfile: (nextProfile) => {
      try {
        if (identifiedId && identifiedId !== nextProfile?.id) {
          client.reset();
          if (consent === "granted") client.opt_in_capturing();
          identifiedId = null;
        }
        profile = nextProfile ? {
          id: nextProfile.id,
          organizationId: nextProfile.organizationId,
          role: nextProfile.role,
          email: profileEmail(nextProfile.email),
        } : null;
        if (identifiedId === profile?.id && initialized && consent === "granted") {
          client.setPersonProperties(personProperties());
        } else {
          identify();
        }
      } catch { /* Authentication remains independent of analytics. */ }
    },
  };
};

const localHost = typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "::1", "[::1]"].includes(window.location.hostname);
const debug = process.env.REACT_APP_POSTHOG_DEBUG === "true";
const mockSession = process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_DEV_AUTH_BYPASS === "true";
const getStorage = () => {
  try { return window.localStorage; } catch { return null; }
};
const analytics = createAnalytics({
  client: posthog,
  token: process.env.REACT_APP_POSTHOG_PROJECT_TOKEN,
  host: process.env.REACT_APP_POSTHOG_HOST,
  enabled: process.env.REACT_APP_POSTHOG_ENABLED === "true" && !mockSession &&
    ((process.env.NODE_ENV === "production" && !localHost) || debug),
  environment: debug ? "development" : process.env.NODE_ENV,
  storage: getStorage(),
  location: typeof window !== "undefined" ? window.location : { pathname: "/", origin: "" },
});

export default analytics;
